import { useState } from 'react';
import { useCMS } from '../../contexts/CMSContext';

export default function CMSToolbar() {
  const {
    isAdmin,
    isEditMode,
    toggleEditMode,
    adminLogout,
    resetToDefaults,
    changeAdminPassword,
    editViewport,
    setEditViewport,
  } = useCMS();
  const [showPwdForm, setShowPwdForm] = useState(false);
  const [pwdData, setPwdData] = useState({ current: '', next: '', confirm: '' });
  const [pwdMsg, setPwdMsg] = useState('');

  if (!isAdmin) return null;

  const handleReset = () => {
    if (window.confirm('¿Restablecer todos los contenidos al original? Esta acción no se puede deshacer.')) {
      resetToDefaults();
    }
  };

  const handleLogout = () => {
    if (window.confirm('¿Cerrar sesión de administrador?')) {
      adminLogout();
    }
  };

  const handleChangePwd = (e: React.FormEvent) => {
    e.preventDefault();
    if (pwdData.next !== pwdData.confirm) {
      setPwdMsg('Las contraseñas nuevas no coinciden.');
      return;
    }
    if (pwdData.next.length < 6) {
      setPwdMsg('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    const ok = changeAdminPassword(pwdData.current, pwdData.next);
    if (ok) {
      setPwdMsg('¡Contraseña actualizada con éxito!');
      setPwdData({ current: '', next: '', confirm: '' });
      setTimeout(() => { setPwdMsg(''); setShowPwdForm(false); }, 2000);
    } else {
      setPwdMsg('La contraseña actual es incorrecta.');
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {isEditMode && (
        <div className="bg-white border border-stone-200 rounded-2xl shadow-xl px-5 py-4 text-sm flex flex-col gap-3 min-w-[240px]">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-[#8B9D83] rounded-full animate-pulse inline-block"></span>
            <p className="text-[#1A1A1A] text-xs font-bold uppercase tracking-wider">Modo Edición Activo</p>
          </div>
          <div className="flex rounded-xl border border-stone-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setEditViewport('desktop')}
              className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${
                editViewport === 'desktop'
                  ? 'bg-[#1A1A1A] text-white'
                  : 'bg-stone-50 text-stone-600 hover:bg-stone-100'
              }`}
            >
              Escritorio
            </button>
            <button
              type="button"
              onClick={() => setEditViewport('mobile')}
              className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${
                editViewport === 'mobile'
                  ? 'bg-[#1A1A1A] text-white'
                  : 'bg-stone-50 text-stone-600 hover:bg-stone-100'
              }`}
            >
              Móvil
            </button>
          </div>
          <p className="text-stone-500 text-xs leading-relaxed">
            En <span className="font-semibold text-stone-700">Móvil</span> editás textos e imágenes solo para pantallas pequeñas; si no definís un valor, se usa el de escritorio.
          </p>
          <p className="text-stone-500 text-xs leading-relaxed">
            Haz clic en cualquier texto o imagen para editarlo. Los cambios se guardan automáticamente.
          </p>
          <div className="flex flex-col gap-1.5 pt-1 border-t border-stone-100">
            <button onClick={handleReset} className="text-xs text-red-500 hover:text-red-700 transition-colors text-left cursor-pointer flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-restart-line"></i> Restablecer original
            </button>
            <button onClick={() => setShowPwdForm(!showPwdForm)} className="text-xs text-stone-500 hover:text-[#8B9D83] transition-colors text-left cursor-pointer flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-key-2-line"></i> Cambiar contraseña
            </button>
            <button onClick={handleLogout} className="text-xs text-stone-500 hover:text-[#1A1A1A] transition-colors text-left cursor-pointer flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-logout-box-line"></i> Cerrar sesión
            </button>
          </div>

          {showPwdForm && (
            <form onSubmit={handleChangePwd} className="flex flex-col gap-2 pt-2 border-t border-stone-100">
              <p className="text-xs font-semibold text-[#1A1A1A]">Cambiar contraseña</p>
              <input type="password" placeholder="Contraseña actual" value={pwdData.current} onChange={(e) => setPwdData((p) => ({ ...p, current: e.target.value }))} className="border border-stone-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#8B9D83]" />
              <input type="password" placeholder="Nueva contraseña" value={pwdData.next} onChange={(e) => setPwdData((p) => ({ ...p, next: e.target.value }))} className="border border-stone-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#8B9D83]" />
              <input type="password" placeholder="Confirmar nueva" value={pwdData.confirm} onChange={(e) => setPwdData((p) => ({ ...p, confirm: e.target.value }))} className="border border-stone-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#8B9D83]" />
              {pwdMsg && <p className={`text-xs ${pwdMsg.includes('éxito') ? 'text-[#8B9D83]' : 'text-red-500'}`}>{pwdMsg}</p>}
              <button type="submit" className="bg-[#1A1A1A] text-white text-xs font-semibold py-2 rounded-lg hover:bg-[#333] transition-colors cursor-pointer whitespace-nowrap">Guardar</button>
            </form>
          )}
        </div>
      )}

      <button
        onClick={toggleEditMode}
        className={`flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap ${
          isEditMode ? 'bg-[#1A1A1A] text-white' : 'bg-[#8B9D83] text-white hover:bg-[#6B7D63]'
        }`}
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className={`${isEditMode ? 'ri-eye-line' : 'ri-edit-2-line'} text-base`}></i>
        </div>
        {isEditMode ? 'Salir de edición' : 'Editar contenido'}
      </button>
    </div>
  );
}
